// Which fulfillment dates an order-window schedule offers, and why a date is not one.
//
// One rule for everyone who asks: the customer page and the admin order modal build
// their date lists from `offeredFulfillmentDates`, and order creation checks a
// submitted date with `fulfillmentDateViolations`. For a fulfillment type the account
// may use, a date has no violations exactly when it is on the offered list.
//
// Dates are calendar dates ("YYYY-MM-DD") on the org's calendar; "today" is the
// org's date at `now`, and a cutoff is the real instant the org's wall clock shows it
// (`getEditDeadlineForOrder`). Nothing here reads the process's time zone.
import {
  calendarDateInTimezone,
  deliveryDaysMask,
  getEditDeadlineForOrder,
  weekdayOfCalendarDate,
  type FulfillmentConfig,
} from "./periods";

export type FulfillmentType = "delivery" | "pickup";

/** The order-window config fields the date rules read — the shape of the resolved config. */
export interface FulfillmentScheduleConfig extends FulfillmentConfig {
  frequency?: string | null;
  customDays?: number | null;
  /** The account's delivery weekdays (bitmask), from the resolved config. Derived from frequency + customDays when absent. */
  deliveryDays?: number | null;
  deliveryMaxLeadDays?: number | null;
  deliveryBlackoutDates?: string[] | null;
  pickupAvailableDays?: number | null;
  pickupMaxLeadDays?: number | null;
  pickupBlackoutDates?: string[] | null;
}

/** Why a submitted date is not one the schedule offers. */
export type FulfillmentDateRule =
  /** Not a calendar date at all. */
  | "invalid_date"
  /** The org, or this account's override, does not offer that fulfillment type. */
  | "type_not_offered"
  /** Before today on the org's calendar. */
  | "past_date"
  /** The order cutoff for the date (lead / prep days before it) has passed. */
  | "cutoff_passed"
  /** Further ahead than the schedule's maximum lead. */
  | "beyond_max_lead"
  /** Not one of the schedule's weekdays. */
  | "weekday"
  /** A blackout date. */
  | "blackout";

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_TIMEZONE = "America/Toronto";

interface Schedule {
  days: number;
  maxLeadDays: number;
  blackouts: Set<string>;
}

// Pickup that follows the delivery schedule uses delivery's days, lead and blackouts —
// and its cutoff, which `getEditDeadlineForOrder` already knows.
function scheduleFor(type: FulfillmentType, config: FulfillmentScheduleConfig): Schedule {
  if (type === "pickup" && !config.pickupFollowsDeliverySchedule) {
    return {
      days: config.pickupAvailableDays ?? 62,
      maxLeadDays: config.pickupMaxLeadDays ?? 7,
      blackouts: new Set(config.pickupBlackoutDates ?? []),
    };
  }
  return {
    days: config.deliveryDays ?? deliveryDaysMask(config),
    maxLeadDays: config.deliveryMaxLeadDays ?? 7,
    blackouts: new Set(config.deliveryBlackoutDates ?? []),
  };
}

function timezoneOf(config: FulfillmentScheduleConfig): string {
  return config.timezone || DEFAULT_TIMEZONE;
}

/** The calendar date `days` after `ymd`. */
export function addCalendarDays(ymd: string, days: number): string {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function calendarDaysBetween(from: string, to: string): number {
  const utc = (ymd: string) => {
    const [year, month, day] = ymd.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((utc(to) - utc(from)) / DAY_MS);
}

/** Whether the account may use a fulfillment type, by the (resolved) config's switches. */
export function isFulfillmentTypeOffered(type: FulfillmentType, config: FulfillmentScheduleConfig): boolean {
  return type === "delivery" ? config.deliveryEnabled ?? true : config.pickupEnabled ?? false;
}

/**
 * The dates the schedule offers for `type` at `now`, soonest first: from today up to
 * the schedule's maximum lead (or `horizonDays`), on its weekdays, not blacked out,
 * whose cutoff has not passed. Does not check that the type itself is offered — the
 * caller picks the type.
 */
export function offeredFulfillmentDates(
  type: FulfillmentType,
  config: FulfillmentScheduleConfig,
  now: Date = new Date(),
  options: { maxDates?: number; horizonDays?: number } = {},
): string[] {
  const schedule = scheduleFor(type, config);
  const horizon = options.horizonDays ?? schedule.maxLeadDays;
  const maxDates = options.maxDates ?? Infinity;
  const today = calendarDateInTimezone(now, timezoneOf(config));

  const dates: string[] = [];
  for (let offset = 0; offset <= horizon && dates.length < maxDates; offset++) {
    const date = addCalendarDays(today, offset);
    if (!(schedule.days & (1 << weekdayOfCalendarDate(date)!))) continue;
    if (schedule.blackouts.has(date)) continue;
    const cutoff = getEditDeadlineForOrder(date, type, config);
    if (!cutoff || now.getTime() >= cutoff.getTime()) continue;
    dates.push(date);
  }
  return dates;
}

/**
 * The dates staff can move an existing order to (the order slip), soonest first: the
 * schedule's last day before today (within two weeks), then every scheduled day from
 * today up to the schedule's maximum lead — whether or not its cutoff has passed.
 * Staff move orders after the customers' cutoff, so tomorrow stays on the list once
 * customers can no longer pick it. Weekdays and blackouts as `offeredFulfillmentDates`;
 * `maxDates` caps the days from today on.
 */
export function staffFulfillmentDates(
  type: FulfillmentType,
  config: FulfillmentScheduleConfig,
  now: Date = new Date(),
  options: { maxDates?: number } = {},
): string[] {
  const schedule = scheduleFor(type, config);
  const maxDates = options.maxDates ?? Infinity;
  const today = calendarDateInTimezone(now, timezoneOf(config));
  const scheduled = (date: string) =>
    (schedule.days & (1 << weekdayOfCalendarDate(date)!)) !== 0 && !schedule.blackouts.has(date);

  const dates: string[] = [];
  for (let back = 1; back <= 14; back++) {
    const date = addCalendarDays(today, -back);
    if (scheduled(date)) {
      dates.push(date);
      break;
    }
  }
  let ahead = 0;
  for (let offset = 0; offset <= schedule.maxLeadDays && ahead < maxDates; offset++) {
    const date = addCalendarDays(today, offset);
    if (!scheduled(date)) continue;
    dates.push(date);
    ahead++;
  }
  return dates;
}

/**
 * Every rule a submitted fulfillment date breaks at `now`; empty when the schedule
 * offers it. `past_date` comes with `cutoff_passed` (a past date's cutoff has passed).
 */
export function fulfillmentDateViolations(
  date: string,
  type: FulfillmentType,
  config: FulfillmentScheduleConfig,
  now: Date = new Date(),
): FulfillmentDateRule[] {
  const weekday = weekdayOfCalendarDate(date);
  if (weekday === null) return ["invalid_date"];

  const rules: FulfillmentDateRule[] = [];
  if (!isFulfillmentTypeOffered(type, config)) rules.push("type_not_offered");

  const schedule = scheduleFor(type, config);
  const daysAhead = calendarDaysBetween(calendarDateInTimezone(now, timezoneOf(config)), date);
  if (daysAhead < 0) rules.push("past_date");
  const cutoff = getEditDeadlineForOrder(date, type, config);
  if (!cutoff || now.getTime() >= cutoff.getTime()) rules.push("cutoff_passed");
  if (daysAhead > schedule.maxLeadDays) rules.push("beyond_max_lead");
  if (!(schedule.days & (1 << weekday))) rules.push("weekday");
  if (schedule.blackouts.has(date)) rules.push("blackout");
  return rules;
}
